import { useTheme } from '@emotion/react';
import { useTheme as useThemeMui } from '@mui/material/styles';

import styled from '@emotion/styled';
import { Box, CardContent, IconButton, Slider, Tooltip, Typography } from '@mui/material';
import { SimpleTab } from '@shared/components/core/button/SimpleTab';
import StyledTextField from '@shared/components/core/field/StyledTextField';
import { FlexColumn } from '@shared/components/core/headers/ProfileHeadingStyles';
import Icons from '@shared/components/core/images/Icons';
import { BodySmall, DisplayXXSmall, LightXSmall } from '@shared/components/core/text/Typography';
import { color } from '@shared/constants/Styles';
import { MacroDisplayEvent } from '@shared/models/deviceProfiles/MacroEvent';
import { MacroDisplayModel } from '@shared/models/deviceProfiles/MacroModel';
import type React from 'react';
import { useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';

interface IStepJoystickProps {
    macro: MacroDisplayModel;
    event: MacroDisplayEvent;
    eventIndex: number;
    isPreview?: boolean;
    onChange: (macro: MacroDisplayModel) => void;
}

interface JoystickConfig {
    joystickLeftMoveX: number;
    joystickLeftMoveY: number;
    joystickRightMoveX: number;
    joystickRightMoveY: number;
    holdTimeMilliseconds?: number;
    isKeyLatch: boolean;
}

const JOYSTICK_RADIUS = 100;
const STICK_RADIUS = 30;
const JOYSTICK_MOVEMENT_SCALE = JOYSTICK_RADIUS - STICK_RADIUS;

export default function JoystickStep({ macro, event, eventIndex, onChange }: IStepJoystickProps) {
    const [config, setConfig] = useState<JoystickConfig>({
        joystickLeftMoveX: event.joystickLeftMoveX ? event.joystickLeftMoveX / 100 : 0,
        joystickLeftMoveY: event.joystickLeftMoveY ? event.joystickLeftMoveY / 100 : 0,
        joystickRightMoveX: event.joystickRightMoveX ? event.joystickRightMoveX / 100 : 0,
        joystickRightMoveY: event.joystickRightMoveY ? event.joystickRightMoveY / 100 : 0,
        holdTimeMilliseconds: event.holdTimeMilliseconds || 100,
        isKeyLatch: event.isKeyLatch ? event.isKeyLatch : event.holdTimeMilliseconds != undefined,
    });

    const { t } = useTranslation();
    const theme = useTheme();
    const muiTheme = useThemeMui();

    const scaleJoystickValue = (value?: number): number => {
        if (!value) return 0;
        value = value * 100; // Scale to percentage
        const clampedValue = Math.max(-100, Math.min(100, value));
        return Math.round(clampedValue);
    };

    const clampToUnitCircle = (x: number, y: number) => {
        const mag = Math.sqrt(x * x + y * y);
        if (mag > 1) {
            return { x: x / mag, y: y / mag };
        }
        return { x, y };
    };

    const clampAndFloatValue = (value: number | undefined): number => {
        if (value === undefined) return 0;
        let clampedValue = Math.max(-1, Math.min(1, value));
        return clampedValue.toFixed(2) ? parseFloat(clampedValue.toFixed(2)) : 0;
    };

    const handleTimeChange = (isLatched: boolean, value?: number) => {
        const updatedMacro = { ...macro };
        const eventToUpdate = { ...updatedMacro.events[eventIndex] };
        const holdTime = value ?? 0;
        eventToUpdate.holdTimeMilliseconds = isLatched ? undefined : holdTime;
        eventToUpdate.isKeyLatch = isLatched;
        setConfig((prev) => ({
            ...prev,
            holdTimeMilliseconds: isLatched ? 0 : holdTime,
        }));
        updatedMacro.events[eventIndex] = eventToUpdate;
        onChange(updatedMacro);
    };

    const handleJoystickMove = useCallback(
        (stick: 'leftStick' | 'rightStick', x: number, y: number) => {
            const updatedMacro = { ...macro };
            const eventToUpdate = { ...updatedMacro.events[eventIndex] };
            const scaledX = scaleJoystickValue(x);
            const scaledY = scaleJoystickValue(y);
            if (stick === 'leftStick') {
                eventToUpdate.joystickLeftMoveX = scaledX;
                eventToUpdate.joystickLeftMoveY = scaledY;
                setConfig((prev) => ({
                    ...prev,
                    joystickLeftMoveX: x,
                    joystickLeftMoveY: y,
                }));
            } else {
                eventToUpdate.joystickRightMoveX = scaledX;
                eventToUpdate.joystickRightMoveY = scaledY;
                setConfig((prev) => ({
                    ...prev,
                    joystickRightMoveX: x,
                    joystickRightMoveY: y,
                }));
            }
            updatedMacro.events[eventIndex] = eventToUpdate;
            if (!eventToUpdate.isKeyLatch && !eventToUpdate.holdTimeMilliseconds) {
                eventToUpdate.isKeyLatch = true;
            }
            onChange(updatedMacro);
        },
        [macro, eventIndex, onChange]
    );

    const handleMouseDown = (stick: 'leftStick' | 'rightStick', event: React.MouseEvent) => {
        const rect = (event.currentTarget as HTMLElement).getBoundingClientRect();
        const centerX = rect.left + rect.width / 2;
        const centerY = rect.top + rect.height / 2;

        const handleMouseMove = (e: MouseEvent) => {
            let x = (e.clientX - centerX) / JOYSTICK_MOVEMENT_SCALE;
            let y = -(e.clientY - centerY) / JOYSTICK_MOVEMENT_SCALE;
            x = clampAndFloatValue(x);
            y = clampAndFloatValue(y);

            handleJoystickMove(stick, x, y);
        };

        const handleMouseUp = () => {
            document.removeEventListener('mousemove', handleMouseMove);
            document.removeEventListener('mouseup', handleMouseUp);
        };

        document.addEventListener('mousemove', handleMouseMove);
        document.addEventListener('mouseup', handleMouseUp);
    };

    const resetJoystick = (stick: 'leftStick' | 'rightStick') => {
        handleJoystickMove(stick, 0, 0);
    };
    const clampedLeft = clampToUnitCircle(config.joystickLeftMoveX, config.joystickLeftMoveY);
    const clampedRight = clampToUnitCircle(config.joystickRightMoveX, config.joystickRightMoveY);
    return (
        <>
            <CardContent>
                {/* Joystick Visualizations */}
                <JoystickContainer>
                    <JoystickWrapper>
                        <DisplayXXSmall>{t('common:event.joystickMove.left')}</DisplayXXSmall>
                        <JoystickBase
                            onMouseDown={(e) => handleMouseDown('leftStick', e)}
                            color={muiTheme.palette.primary.main}>
                            <JoystickStick
                                x={clampedLeft.x}
                                y={clampedLeft.y}
                                active={!!(config.joystickLeftMoveX || config.joystickLeftMoveY)}
                                color={muiTheme.palette.primary.main}
                            />
                        </JoystickBase>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <StatusIndicator active={!!(config.joystickLeftMoveX || config.joystickLeftMoveY)} />
                            <LightXSmall>
                                X: {config.joystickLeftMoveX.toFixed(2)}, Y: {config.joystickLeftMoveY.toFixed(2)}
                            </LightXSmall>

                            <Tooltip title={t('common:reset')}>
                                <IconButton size="small" onClick={() => resetJoystick('leftStick')}>
                                    <Icons name="sync" size={18} color={theme.color.textBody} />
                                </IconButton>
                            </Tooltip>
                        </Box>
                    </JoystickWrapper>

                    <JoystickWrapper>
                        <DisplayXXSmall>{t('common:event.joystickMove.right')}</DisplayXXSmall>
                        <JoystickBase
                            onMouseDown={(e) => handleMouseDown('rightStick', e)}
                            color={muiTheme.palette.secondary.main}>
                            <JoystickStick
                                x={clampedRight.x}
                                y={clampedRight.y}
                                active={!!(config.joystickRightMoveX || config.joystickRightMoveY)}
                                color={muiTheme.palette.secondary.main}
                            />
                        </JoystickBase>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <StatusIndicator active={!!(config.joystickRightMoveX || config.joystickRightMoveY)} />
                            <LightXSmall>
                                X: {config.joystickRightMoveX.toFixed(2)}, Y: {config.joystickRightMoveY.toFixed(2)}
                            </LightXSmall>
                            <Tooltip title="Reset Position">
                                <IconButton size="small" onClick={() => resetJoystick('rightStick')}>
                                    <Icons name="sync" size={18} color={theme.color.textBody} />
                                </IconButton>
                            </Tooltip>
                        </Box>
                    </JoystickWrapper>
                </JoystickContainer>

                {/* Control Panel */}
                <>
                    {/* Left Stick Controls */}
                    <AxisControl>
                        <Typography id={`leftX${macro.id}`} variant="body2" sx={{ minWidth: 70 }}>
                            {t('common:event.joystickMove.leftX')}:
                        </Typography>

                        <Slider
                            aria-labelledby={`leftX${macro.id}`}
                            value={config.joystickLeftMoveX}
                            onChange={(_, value) =>
                                handleJoystickMove('leftStick', value as number, config.joystickLeftMoveY || 0)
                            }
                            min={-1}
                            max={1}
                            step={0.01}
                            sx={{ flex: 1 }}
                            color="primary"
                        />

                        <Typography variant="body2" sx={{ minWidth: 50, textAlign: 'right' }}>
                            {config.joystickLeftMoveX || 0}
                        </Typography>
                    </AxisControl>

                    <AxisControl>
                        <Typography id={`leftY${macro.id}`} variant="body2" sx={{ minWidth: 70 }}>
                            {t('common:event.joystickMove.leftY')}:
                        </Typography>
                        <Slider
                            aria-labelledby={`leftY${macro.id}`}
                            value={config.joystickLeftMoveY}
                            onChange={(_, value) =>
                                handleJoystickMove('leftStick', config.joystickLeftMoveX || 0, value as number)
                            }
                            min={-1}
                            max={1}
                            step={0.01}
                            sx={{ flex: 1 }}
                            color="primary"
                        />
                        <Typography variant="body2" sx={{ minWidth: 50, textAlign: 'right' }}>
                            {config.joystickLeftMoveY}
                        </Typography>
                    </AxisControl>

                    {/* Right Stick Controls */}
                    <AxisControl>
                        <Typography id={`rightX${macro.id}`} variant="body2" sx={{ minWidth: 70 }}>
                            {t('common:event.joystickMove.rightX')}:
                        </Typography>
                        <Slider
                            aria-labelledby={`rightX${macro.id}`}
                            value={config.joystickRightMoveX}
                            onChange={(_, value) =>
                                handleJoystickMove('rightStick', value as number, config.joystickRightMoveY || 0)
                            }
                            min={-1}
                            max={1}
                            step={0.01}
                            sx={{ flex: 1 }}
                            color="secondary"
                        />
                        <Typography variant="body2" sx={{ minWidth: 50, textAlign: 'right' }}>
                            {config.joystickRightMoveX}
                        </Typography>
                    </AxisControl>

                    <AxisControl>
                        <Typography id={`rightY${macro.id}`} variant="body2" sx={{ minWidth: 70 }}>
                            {t('common:event.joystickMove.rightY')}:
                        </Typography>
                        <Slider
                            aria-labelledby={`rightY${macro.id}`}
                            value={config.joystickRightMoveY}
                            onChange={(_, value) =>
                                handleJoystickMove('rightStick', config.joystickRightMoveX || 0, value as number)
                            }
                            min={-1}
                            max={1}
                            step={0.01}
                            sx={{ flex: 1 }}
                            color="secondary"
                        />
                        <Typography variant="body2" sx={{ minWidth: 50, textAlign: 'right' }}>
                            {config.joystickRightMoveY}
                        </Typography>
                    </AxisControl>
                    <FlexColumn>
                        <StyledRow>
                            <SimpleTab
                                isActive={!!event.isKeyLatch || event.isKeyLatch == undefined}
                                onClick={() => handleTimeChange(true)}>
                                {t('common:event.keyPress.latch')}
                            </SimpleTab>
                            <SimpleTab
                                isActive={
                                    !(event.isKeyLatch || event.isKeyLatch == undefined) &&
                                    event.holdTimeMilliseconds != undefined
                                }
                                onClick={() => handleTimeChange(false, 0)}>
                                {t('common:event.keyPress.hold')}
                            </SimpleTab>
                        </StyledRow>
                        {!event.isKeyLatch && event.isKeyLatch != undefined ? (
                            <StyledTextField
                                fullWidth
                                label={t('common:event.hold')}
                                type="number"
                                value={config.holdTimeMilliseconds}
                                placeholder={t('common:event.hold')}
                                onChange={(e) => handleTimeChange(false, Number(e.target.value))}
                            />
                        ) : (
                            <BodySmall>{t('common:event.joystickMove.latch')} </BodySmall>
                        )}
                    </FlexColumn>
                </>
            </CardContent>
        </>
    );
}

const JoystickContainer = styled.div`
    display: flex;
    gap: 40px;
    justify-content: space-between;
    margin: 1rem auto;
    flex-wrap: wrap;
    max-width: 600px;
    justify-self: center;
`;

const JoystickWrapper = styled.div`
    display: flex;
    flex-direction: column;
    align-items: center;
    margin: 0 auto;
    gap: 16px;
`;

const JoystickBase = styled.div<{ color: string }>`
    width: 200px;
    height: 200px;
    border-radius: 50%;
    background: ${(props) => props.color}20;
    border: 2px solid ${(props) => props.color};
    position: relative;
    cursor: pointer;
    transition: all 0.3s ease;

    &:hover {
        border: 2px solid ${(props) => props.color};
    }
`;

const JoystickStick = styled.div<{ x: number; y: number; active: boolean; color?: string }>`
    width: 60px;
    height: 60px;
    border-radius: 50%;
    background: ${(props) => (props.active ? props.color || props.theme.color.secondary : props.theme.color.textBody)};
    position: absolute;
    top: 50%;
    left: 50%;
    transform: translate(
        calc(-50% + ${(props) => props.x * JOYSTICK_MOVEMENT_SCALE}px),
        calc(-50% + ${(props) => -props.y * JOYSTICK_MOVEMENT_SCALE}px)
    );
    transition: all 0.2s ease;
    cursor: grab;

    &:active {
        cursor: grabbing;
        transform: translate(
                calc(-50% + ${(props) => props.x * JOYSTICK_MOVEMENT_SCALE}px),
                calc(-50% + ${(props) => -props.y * JOYSTICK_MOVEMENT_SCALE}px)
            )
            scale(0.95);
    }
`;

const AxisControl = styled.div`
    display: flex;
    align-items: center;
    gap: 0.5rem;
    padding: 0.5rem;
`;

const StatusIndicator = styled.div<{ active: boolean }>`
    width: 12px;
    height: 12px;
    border-radius: 50%;
    background: ${(props) => (props.active ? color.success : props.theme.color.textBody)};
    transition: all 0.3s ease;
`;

const StyledRow = styled.div`
    display: flex;
    flex-direction: row;
    flex-wrap: wrap;
    gap: 0.5rem;
    align-items: center;
    justify-content: flex-start;
    padding-bottom: 0.5rem;
`;
